/**
 * Adapter functions that transform raw backend responses (backendTypes.ts)
 * into the frontend types (types.ts) consumed by components.
 */

import { differenceInCalendarDays, parseISO } from "date-fns";
import type {
  BackendPageResponse,
  BackendPricingRequestItem,
  BackendItineraryItem,
  BackendItineraryListResponse,
  BackendMarketSnapshot,
  BackendSummaryResponse,
  BackendAuditItem,
  BackendAuditListResponse,
  BackendGuardrailResult,
  GuardrailStatus,
  BackendFormulaBreakdown,
  BackendPropertyConfig,
  BackendPricingRequestDetailWithContext,
  BackendGprtContextRequestedItinerary,
  BackendGprtContextCurrentItinerary,
  BackendGprtContextFinalQuotedItinerary,
  BackendSarahReviewStatus,
} from "./backendTypes";
import { PLATFORM_MAP } from "./types";
import type {
  GpcPropertyConfig,
  FloorType,
  CeilingType,
  BandConfig,
  DemandBand,
  PricingRequestListItem,
  PricingRequestListResponse,
  PricingRequestDetail,
  PricingRequestStatus,
  SarahResumeStatus,
  PricingDecision,
  PricingMethod,
  CommissionType,
  StrategyOutcome,
  QuotedItineraryItem,
  QuotedItinerariesResponse,
  MarketSnapshot,
  RequestSummaryResponse,
  RoomTypeSummaryItem,
  AuditItem,
  AuditsResponse,
  GuardrailResult,
  FormulaBreakdown,
  CreatePricingRequestPayload,
  ListRequestsParams,
  PricingRequestDetailWithGprtContext,
  GprtContextRequestedItinerary,
  GprtContextCurrentItinerary,
  GprtContextFinalQuotedItinerary,
  SarahReviewStatus,
  GpcPendingReviewType,
} from "./types";
import { adaptAttachment } from "../../grouppricingrequests/ActionApis/adapters";
import { dedupeContainedItineraryRanges } from "../components/itineraryWarnings";

const platformName = (id: number): string => PLATFORM_MAP[id] ?? "Other";

const toStatus = (raw: string): PricingRequestStatus =>
  raw as PricingRequestStatus;

const toNull = <T>(val: T | undefined): T | null =>
  val === undefined ? null : val;

const computeNightCount = (start: string, end: string): number =>
  differenceInCalendarDays(parseISO(end), parseISO(start));

// Normalizes a response that may be a bare array (legacy) or an { items } envelope.
const safeListItems = <TItem>(
  raw: { items?: TItem[] } | TItem[] | null | undefined
): TItem[] => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  return raw.items ?? [];
};

// Backend "TWELVE_MONTH_PRICING" maps to frontend "12M_PRICING".
const PRICING_METHOD_MAP: Record<string, PricingMethod> = {
  GPC: "GPC",
  TWELVE_MONTH_PRICING: "12M_PRICING",
  LLM: "LLM",
  HITL: "HITL",
};

const adaptPricingMethod = (backendMethod: string): PricingMethod =>
  PRICING_METHOD_MAP[backendMethod] ?? (backendMethod as PricingMethod);

export const generatePlatformRfpId = (): string => {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 6);
  return `GPC-${ts}-${rand}`.toUpperCase();
};

// ══════════════════════════════════════════════════
// List Response
// ══════════════════════════════════════════════════

// Shared by adaptListItem and adaptDetailResponse — PricingRequestListItem is a strict subset of PricingRequestDetail.
const adaptCommonRequestFields = (raw: BackendPricingRequestItem) => ({
  id: raw.id,
  propertyId: raw.propertyId,
  platformRfpId: toNull(raw.platformRfpId),
  requestSource: raw.requestSource,
  requestedBy: raw.requestedBy ?? "",
  platform: { id: raw.platformId, name: platformName(raw.platformId) },
  requestedStartDate: raw.requestedStartDate,
  requestedEndDate: raw.requestedEndDate,
  nightCount: computeNightCount(raw.requestedStartDate, raw.requestedEndDate),
  requestedTotalRooms: raw.requestedTotalRooms,
  quotedTotalRooms: toNull(raw.quotedTotalRooms),
  quotedTotalPrice: toNull(raw.quotedTotalPrice),
  strategyUsed: toNull(raw.strategySummary),
  status: toStatus(raw.status),
  isPartialAllocation: raw.isPartialAllocation,
  durationMs: toNull(raw.durationMs),
  createdAt: raw.createdAt,
  decision: toNull(raw.decision) as PricingDecision | null,
  decisionComment: toNull(raw.decisionComment),
  rateAcknowledgedBy: toNull(raw.rateAcknowledgedBy),
  pendingGpcReviewType: toNull(raw.pendingGpcReviewType),
});

const adaptListItem = (
  raw: BackendPricingRequestItem
): PricingRequestListItem => adaptCommonRequestFields(raw);

export const adaptListResponse = (
  raw: BackendPageResponse<BackendPricingRequestItem>
): PricingRequestListResponse => ({
  content: (raw.content ?? []).map(adaptListItem),
  page: raw.number,
  size: raw.size,
  totalElements: raw.totalElements,
  totalPages: raw.totalPages,
});

// ══════════════════════════════════════════════════
// Guardrail Results (audit items)
// ══════════════════════════════════════════════════

const adaptGuardrailResults = (
  raw: Record<string, BackendGuardrailResult> | BackendGuardrailResult[],
  legacyFallbackStatus: GuardrailStatus = "NOT_EVALUATED"
): Record<string, GuardrailResult> => {
  const record: Record<string, GuardrailResult> = {};
  // Pre-PT-4070 rows stored `passed` (boolean) instead of `status`; if both are
  // absent, fall back to the caller's attempt-level outcome.
  const resolveGuardrailStatus = (g: BackendGuardrailResult): GuardrailStatus =>
    g.status ??
    (g.passed == null ? legacyFallbackStatus : g.passed ? "SUCCESS" : "FAILED");
  const toRow = (key: string, g: BackendGuardrailResult): GuardrailResult => ({
    label: g.name ?? key,
    status: resolveGuardrailStatus(g),
    value: toNull(g.value),
    threshold: toNull(g.threshold),
    detail: toNull(g.detail),
  });
  if (Array.isArray(raw)) {
    // Legacy format: array of { name, status, value, threshold, detail }
    for (const g of raw) {
      record[g.name] = toRow(g.name, g);
    }
  } else {
    // Envelope format: Record<string, { status, value, threshold, detail }>
    for (const [key, g] of Object.entries(raw)) {
      record[key] = toRow(key, g);
    }
  }
  return record;
};

// ══════════════════════════════════════════════════
// Detail Response
// ══════════════════════════════════════════════════

export const adaptDetailResponse = (
  raw: BackendPricingRequestItem
): PricingRequestDetail => ({
  ...adaptCommonRequestFields(raw),
  commissionType: toNull(raw.commissionType) as CommissionType | null,
  requestedCommissionValue: toNull(raw.requestedCommissionValue),
  rebateType: toNull(raw.rebateType) as CommissionType | null,
  requestedRebateValue: toNull(raw.requestedRebateValue),
  quotedStartDate: toNull(raw.quotedStartDate),
  quotedEndDate: toNull(raw.quotedEndDate),
  quotedCommissionValue: toNull(raw.quotedCommissionValue),
  quotedRebateValue: toNull(raw.quotedRebateValue),
  pricingReasoning: toNull(raw.pricingReasoning),
  errorReason: toNull(raw.errorReason),
  errorCode: toNull(raw.errorCode),
  traceId: toNull(raw.traceId),
  updatedAt: raw.updatedAt ?? raw.createdAt,
  requestedComments: toNull(raw.requestedComments),
  sarahResumeStatus: toNull(raw.sarahResumeStatus) as SarahResumeStatus | null,
  sarahResumeError: toNull(raw.sarahResumeError),
});

// ══════════════════════════════════════════════════
// GPRT Context (endpoint 6)
// ══════════════════════════════════════════════════

const adaptGprtContextRequestedItinerary = (
  raw: BackendGprtContextRequestedItinerary
): GprtContextRequestedItinerary => ({
  id: raw.id,
  checkinDate: raw.checkinDate,
  checkoutDate: raw.checkoutDate,
  peakRoomCount: toNull(raw.peakRoomCount),
  roomTypeText: toNull(raw.roomTypeText),
});

const adaptGprtContextCurrentItinerary = (
  raw: BackendGprtContextCurrentItinerary
): GprtContextCurrentItinerary => ({
  id: raw.id,
  checkinDate: raw.checkinDate,
  checkoutDate: raw.checkoutDate,
  roomCategory: raw.roomCategory,
  roomCount: raw.roomCount,
});

const adaptGprtContextFinalQuotedItinerary = (
  raw: BackendGprtContextFinalQuotedItinerary
): GprtContextFinalQuotedItinerary => ({
  checkinDate: raw.checkin_date,
  checkoutDate: raw.checkout_date,
  roomTypeText: toNull(raw.room_type_text),
  roomCount: toNull(raw.room_count),
  price: toNull(raw.price),
});

export const adaptPricingRequestDetailWithContext = (
  raw: BackendPricingRequestDetailWithContext
): PricingRequestDetailWithGprtContext => ({
  pricingRequest: adaptDetailResponse(raw.pricingRequest),
  // rfp.request_itineraries can contain redundant sub-range rows alongside
  // the actual requested line (e.g. a full 18th–20th line plus leftover
  // 18th–18th/19th–19th rows) — collapsed once here so every consumer (the
  // read-only display, Edit & Recalculate seeding, Manual Rate seeding)
  // shows only the real requested itinerary instead of each re-deriving it.
  requestedItineraries: dedupeContainedItineraryRanges(
    (raw.requestedItineraries ?? [])
      .map(adaptGprtContextRequestedItinerary)
      // Meeting space rides in this array too but isn't a bookable room, so it
      // has no room code and would seed Edit & Recalculate with a line that can
      // never resolve to one.
      .filter((it) => it.roomTypeText !== "MEETING_ROOM")
  ),
  currentRequestedItineraries: (raw.currentRequestedItineraries ?? []).map(
    adaptGprtContextCurrentItinerary
  ),
  finalQuotedItineraries: (raw.finalQuotedItineraries ?? []).map(
    adaptGprtContextFinalQuotedItinerary
  ),
  attachments: (raw.attachments ?? []).map(adaptAttachment),
});

export const adaptSarahReviewStatus = (
  raw: BackendSarahReviewStatus
): SarahReviewStatus => ({
  pendingGpcReviewType: toNull(
    raw.pendingGpcReviewType
  ) as GpcPendingReviewType | null,
  sarahResumeStatus: toNull(raw.sarahResumeStatus) as SarahResumeStatus | null,
  sarahResumeError: toNull(raw.sarahResumeError),
});

// ══════════════════════════════════════════════════
// Itineraries Response
// ══════════════════════════════════════════════════

const adaptMarketSnapshot = (raw: BackendMarketSnapshot): MarketSnapshot => ({
  capturedAt: raw.capturedAt ?? null,
  // `?? null` only on the array itself — an empty array must survive as `[]`,
  // since captured-with-no-coverage reads differently to never-captured.
  roomTypeAvailability:
    raw.roomTypeAvailability?.map((entry) => ({
      roomTypeCode: entry.roomTypeCode,
      availableRooms: entry.availableRooms ?? null,
    })) ?? null,
  occupiedRooms: raw.occupiedRooms ?? null,
  inventory: raw.inventory ?? null,
  ooo: raw.ooo ?? 0,
  occupancyPct:
    raw.inventory && raw.inventory > 0
      ? ((raw.occupiedRooms ?? 0) / raw.inventory) * 100
      : 0,
  // Backend sends this as a 0–1 fraction, same raw scale occupancyPct above
  // converts from — scaled here too so both render on the same percent basis.
  sdlyOccupancy: (raw.sdlyOccupancy ?? 0) * 100,
  adr: raw.adr ?? 0,
  sdlyAdr: raw.sdlyAdr ?? 0,
  sdllyAdr: raw.sdllyAdr ?? 0,
  stlyAdr: raw.stlyAdr ?? 0,
  stlyOtb: raw.stlyOtb ?? 0,
  sdlyOtb: raw.sdlyOtb ?? 0,
  pace: raw.pace ?? 0,
  hotelBarRate: raw.hotelBarRate ?? 0,
  hotelLowestRate: raw.hotelLowestRate ?? 0,
  avgCompsetBarRate: raw.avgCompsetBarRate ?? 0,
  avgCompsetExpediaRate: raw.avgCompsetExpediaRate ?? 0,
  totalBudget: raw.totalBudget ?? 0,
  expediaRoomCode: toNull(raw.expediaRoomCode),
  blackoutReason: toNull(raw.blackoutReason),
});

// adaptMarketSnapshot's output for an all-absent snapshot, named for reuse.
const EMPTY_MARKET_SNAPSHOT: MarketSnapshot = {
  capturedAt: null,
  roomTypeAvailability: null,
  occupiedRooms: null,
  inventory: null,
  ooo: 0,
  occupancyPct: 0,
  sdlyOccupancy: 0,
  adr: 0,
  sdlyAdr: 0,
  sdllyAdr: 0,
  stlyAdr: 0,
  stlyOtb: 0,
  sdlyOtb: 0,
  pace: 0,
  hotelBarRate: 0,
  hotelLowestRate: 0,
  avgCompsetBarRate: 0,
  avgCompsetExpediaRate: 0,
  totalBudget: 0,
  expediaRoomCode: null,
  blackoutReason: null,
};

const adaptItineraryItem = (
  raw: BackendItineraryItem
): QuotedItineraryItem => ({
  stayDate: raw.stayDate,
  roomType: raw.roomType ?? null,
  requestedRooms: raw.marketSnapshot?.requestedRooms ?? raw.roomCount ?? null,
  allocatedRooms: raw.roomCount ?? null,
  sellRate: raw.sellRate ?? null,
  pricingMethod: raw.pricingMethod
    ? adaptPricingMethod(raw.pricingMethod)
    : null,
  allocationReasoning: toNull(raw.allocationReasoning),
  marketSnapshot: raw.marketSnapshot
    ? adaptMarketSnapshot(raw.marketSnapshot)
    : EMPTY_MARKET_SNAPSHOT,
});

export const adaptItinerariesResponse = (
  raw: BackendItineraryListResponse | undefined,
  requestId: string
): QuotedItinerariesResponse => {
  const items = safeListItems(raw).map(adaptItineraryItem);
  const dates = items.map((i) => i.stayDate).sort();
  return {
    requestId,
    requestedStartDate: dates[0] ?? "",
    requestedEndDate: dates[dates.length - 1] ?? "",
    items,
  };
};

// ══════════════════════════════════════════════════
// Summary Response
// ══════════════════════════════════════════════════

export const adaptSummaryResponse = (
  raw: BackendSummaryResponse,
  requestId: string
): RequestSummaryResponse => ({
  requestId,
  dateRangeSummary: {
    totalNights: raw.totalNights,
    overallWeightedAvgSellRate: raw.weightedAvgSellRate ?? 0,
    minSellRateAnyNight: raw.minSellRate ?? 0,
    maxSellRateAnyNight: raw.maxSellRate ?? 0,
    totalQuotedPrice: raw.totalBookingValue ?? 0,
  },
  roomTypeSummary: (raw.roomTypeSummaries ?? []).map(
    (rt): RoomTypeSummaryItem => ({
      roomType: rt.roomType,
      avgSellRateOverRange: rt.avgSellRate ?? 0,
      minSellRate: rt.minSellRate ?? 0,
      maxSellRate: rt.maxSellRate ?? 0,
      nightsWithAllocation: raw.totalNights,
    })
  ),
});

// ══════════════════════════════════════════════════
// Audits Response
// ══════════════════════════════════════════════════

const adaptFormulaBreakdown = (
  raw: BackendFormulaBreakdown
): FormulaBreakdown => ({
  ceiling: raw.ceiling ?? 0,
  floorRbrc: raw.floorRbrc ?? 0,
  floorMinAdr: raw.floorMinAdr ?? 0,
  floor: raw.floor ?? 0,
  width: raw.width ?? 0,
  budgetOccPercent: raw.budgetOccPercent ?? 0,
  demandFactor: raw.demandFactor ?? 0,
  dampenerRatio: raw.dampenerRatio ?? 0,
  dampenerFactor: raw.dampenerFactor ?? 0,
  rawPrice: raw.rawPrice ?? 0,
  finalPrice: raw.finalPrice ?? 0,
  differential: raw.differential ?? 0,
  // Fall back to finalPrice + differential so payloads predating this field still render.
  priceWithDifferential:
    raw.priceWithDifferential ??
    (raw.finalPrice ?? 0) + (raw.differential ?? 0),
  sellPrice: raw.sellPrice ?? 0,
  cappedPrice: raw.cappedPrice ?? raw.sellPrice ?? 0,
  commissionType: toNull(raw.commissionType),
  rebateType: toNull(raw.rebateType),
  commissionValue: toNull(raw.commissionValue),
  rebateValue: toNull(raw.rebateValue),
  degenerateCorridor: raw.degenerateCorridor ?? false,
  widthNote: toNull(raw.widthNote),
});

const adaptAuditItem = (raw: BackendAuditItem): AuditItem => ({
  id: raw.id,
  stayDate: raw.stayDate,
  roomType: raw.roomType,
  pricingMethod: adaptPricingMethod(raw.pricingMethod),
  strategyOutcome: raw.strategyOutcome as StrategyOutcome,
  attemptOrder: raw.attemptOrder,
  allocatedRooms: toNull(raw.allocatedRooms),
  sellRate: toNull(raw.sellRate),
  finalSellRate: toNull(raw.finalSellRate),
  compsetCapApplied: raw.compsetCapApplied ?? false,
  compsetCapRate: toNull(raw.compsetCapRate),
  errorReason: toNull(raw.errorReason),
  blackoutReason: toNull(raw.blackoutReason),
  durationMs: raw.durationMs ?? 0,
  guardrailResults: raw.guardrailResults
    ? adaptGuardrailResults(
        raw.guardrailResults,
        raw.strategyOutcome === "SUCCESS" ? "SUCCESS" : undefined
      )
    : null,
  formulaBreakdown: raw.formulaBreakdown
    ? adaptFormulaBreakdown(raw.formulaBreakdown)
    : null,
});

export const adaptAuditsResponse = (
  raw: BackendAuditListResponse | undefined,
  requestId: string
): AuditsResponse => {
  const items = safeListItems(raw).map(adaptAuditItem);
  const failedAttempts = items.filter(
    (i) => i.strategyOutcome !== "SUCCESS"
  ).length;
  const hasGuardrailFailure = items.some((i) =>
    i.guardrailResults
      ? Object.values(i.guardrailResults).some((g) => g.status === "FAILED")
      : false
  );
  return {
    requestId,
    hasWarnings: failedAttempts > 0 || hasGuardrailFailure,
    totalAttempts: items.length,
    failedAttempts,
    items,
  };
};

// ══════════════════════════════════════════════════
// Property Config
// ══════════════════════════════════════════════════

const FLOOR_TYPES: readonly FloorType[] = [
  "CALCULATED_FROM_ADR",
  "FETCHED_FROM_SEASONS",
  "BOTH_HIGHER_OF_TWO",
  "BOTH_LOWER_OF_TWO",
];

const CEILING_TYPES: readonly CeilingType[] = [
  "FETCHED_FROM_SDLY_ADR",
  "FETCHED_FROM_SEASONS",
  "BOTH_HIGHER_OF_TWO",
  "BOTH_LOWER_OF_TWO",
];

/**
 * Unrecognised enum values become null rather than being passed through — the
 * radio group can render "nothing selected", but it can't render a value that
 * isn't one of its options.
 */
const asEnum = <T extends string>(
  raw: string | undefined,
  allowed: readonly T[]
): T | null =>
  raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : null;

const parseBandConfig = (jsonStr: string | undefined): BandConfig => {
  const defaultBand: DemandBand = { value: 0, threshold: 0 };
  const defaultConfig: BandConfig = {
    low: defaultBand,
    mid: defaultBand,
    high: defaultBand,
  };
  if (!jsonStr) return defaultConfig;

  try {
    const parsed = JSON.parse(jsonStr);
    if (!parsed?.low || !parsed.mid || !parsed.high) return defaultConfig;
    const band = (b: Partial<DemandBand>): DemandBand => ({
      value: b.value ?? 0,
      threshold: b.threshold ?? 0,
    });
    return {
      low: band(parsed.low),
      mid: band(parsed.mid),
      high: band(parsed.high),
    };
  } catch {
    return defaultConfig;
  }
};

/** Inverse of parseBandConfig — the PATCH contract takes the same stringified JSON. */
export const serializeBandConfig = (config: BandConfig): string =>
  JSON.stringify(config);

export const adaptPropertyConfig = (
  raw: BackendPropertyConfig
): GpcPropertyConfig => ({
  propertyId: raw.propertyId,
  isGpcActive: raw.isGpcActive,
  totalRooms: raw.totalRooms ?? null,
  reqToTotalRatio: raw.reqToTotalRatio ?? null,
  pgOtbRatio: raw.pgOtbRatio ?? null,
  budgetOccRatio: raw.budgetOccRatio ?? null,
  otaRateRangeThreshold: raw.otaRateRangeThreshold ?? null,
  compsetRateRangeThreshold: raw.compsetRateRangeThreshold ?? null,
  demandConfig: parseBandConfig(raw.demandConfig),
  dampeningConfig: parseBandConfig(raw.dampeningConfig),
  floorType: asEnum(raw.floorType, FLOOR_TYPES),
  ceilingType: asEnum(raw.ceilingType, CEILING_TYPES),
});

// ══════════════════════════════════════════════════
// Create Payload
// ══════════════════════════════════════════════════

export const adaptCreatePayload = (
  payload: CreatePricingRequestPayload,
  propertyId: string
) => {
  const omitCommission =
    !payload.commissionType || payload.commissionType === "NONE";
  const omitRebate = !payload.rebateType || payload.rebateType === "NONE";
  return {
    ...payload,
    propertyId,
    commissionType: omitCommission ? undefined : payload.commissionType,
    requestedCommissionValue: omitCommission
      ? undefined
      : payload.requestedCommissionValue,
    rebateType: omitRebate ? undefined : payload.rebateType,
    requestedRebateValue: omitRebate ? undefined : payload.requestedRebateValue,
    // Snake_case exception — see contract's "Casing gotcha" callout.
    requestedComments: undefined,
    requested_comments: payload.requestedComments || undefined,
  };
};

// ══════════════════════════════════════════════════
// List Query Params
// ══════════════════════════════════════════════════

export const adaptListParams = (
  params?: ListRequestsParams
): Record<string, string | number | boolean | undefined> | undefined => {
  if (!params) return undefined;
  const { requestSource, ...rest } = params;
  return {
    ...rest,
    source: Array.isArray(requestSource)
      ? requestSource.join(",")
      : requestSource,
  };
};
